/**
 * Save bar at the bottom of the Edit screen.
 *
 * Three controls, mirroring the mock's button row:
 *   - Save     — persists ONLY the active NDC
 *   - Save All — persists every dirty NDC atomically (bulk save)
 *   - Cancel   — drops every working copy, after an explicit
 *                confirmation modal
 *
 * Notes are optional: adding a note for the active NDC is always
 * available in the right-hand Notes panel, but Save / Save All are no
 * longer gated by a per-NDC "fresh note since last save" requirement.
 */

import { useState } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';

import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';

import { useEditStore } from './editStoreContext';
import styles from './saveBar.module.scss';

const SaveBar = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  if (!slice) return null;

  const activeNDC = slice.ndc;
  const activeStatus = store.saveStatusByNDC.get(activeNDC) ?? { kind: 'idle' };
  const isActiveDirty = store.isDirty(activeNDC);
  // Only true when the user clicked **Save** (single-NDC). `saveAllDirty`
  // also flips per-NDC status to "saving" for every dirty NDC, but the
  // operator clicked Save All — so the spinner belongs on Save All, not
  // on Save. Subtracting `isSavingAll` here lets the loader prop pick the
  // right button. Both buttons are still disabled together below.
  const isActiveSaving = activeStatus.kind === 'saving' && !store.isSavingAll;
  const anyDirty = store.anyDirty;

  const handleSaveOne = () => {
    void store.saveOne(activeNDC);
  };

  const handleSaveAll = () => {
    void store.saveAllDirty();
  };

  const openCancelConfirm = () => setCancelConfirmVisible(true);
  const closeCancelConfirm = () => setCancelConfirmVisible(false);
  const handleCancelConfirmed = () => {
    store.cancelAllEdits();
    closeCancelConfirm();
  };

  // Disable conditions are pure "is there work to do / is something
  // in flight" — no note-debt requirement anymore. Notes panel is
  // optional and never blocks save.
  //
  // While any save is in flight (either single or all), BOTH buttons
  // stay disabled to prevent a second click landing mid-flight; only
  // the clicked button shows the spinner (see `isActiveSaving` /
  // `isSavingAll` split above).
  const anySaveInFlight = isActiveSaving || store.isSavingAll || activeStatus.kind === 'saving';
  const saveDisabled = !isActiveDirty || anySaveInFlight;
  const saveAllDisabled = !anyDirty || anySaveInFlight;
  const cancelDisabled = !anyDirty || anySaveInFlight;

  const saveHint = !isActiveDirty ? 'No changes to save for this NDC.' : null;
  const saveAllHint = !anyDirty ? 'No NDCs have unsaved changes.' : null;

  return (
    <div className={styles.wrap}>
      <div className={styles.statusRow}>
        {activeStatus.kind === 'error' && (
          <span className={styles.errorMsg}>Save failed: {activeStatus.message}</span>
        )}
        {store.saveAllError && <span className={styles.errorMsg}>{store.saveAllError}</span>}
        {activeStatus.kind === 'success' && <span className={styles.successMsg}>Saved.</span>}
      </div>
      <div className={styles.buttonRow}>
        <Button
          className={styles.cancelButton}
          onClick={openCancelConfirm}
          disabled={cancelDisabled}
          aria-label="Cancel all unsaved edits"
        >
          Cancel
        </Button>
        <Button
          className={styles.saveButton}
          loading={isActiveSaving}
          disabled={saveDisabled}
          onClick={handleSaveOne}
          aria-label="Save current NDC"
          tooltip={saveHint ?? undefined}
          tooltipOptions={{ position: 'top' }}
        >
          Save
        </Button>
        <Button
          className={styles.finishButton}
          loading={store.isSavingAll}
          disabled={saveAllDisabled}
          onClick={handleSaveAll}
          aria-label="Save all dirty NDCs atomically"
          tooltip={saveAllHint ?? undefined}
          tooltipOptions={{ position: 'top' }}
        >
          Save All
        </Button>
      </div>

      <ConfirmationModal
        visibility={cancelConfirmVisible}
        modalHeading="Discard unsaved edits?"
        modalContent={'Cancel will revert every dirty NDC to the last saved state. ' + 'Continue?'}
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        handleCancel={closeCancelConfirm}
        handleConfirm={handleCancelConfirmed}
      />
    </div>
  );
});

export default SaveBar;
